import React, { useCallback, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'react-toastify';
import {
  useCheckoutCartState,
  useCheckoutComMachine,
} from '@monofront/business/contexts';
import {
  CHECKOUTCOM_CARD_FORM_TRANSITIONS,
  CHECKOUT_COM_CARD_FORM_STATES,
} from '@monofront/business/stateMachines';
import Modal from '@monofront/design-system/organisms/Modal';

import { LF_WINDOW_MESSAGE } from '@constants/message';
import logger from '@utils/logger';
import { useAuthentication } from '@context/businessContainer';
import { SubscriptionStatus } from '@monofront/types';
import { isMobileApp } from '@utils/environment';
import { sendMessageToMobile } from '@utils/mobile';

interface ICheckoutComModal3DS {
  on3dsSuccess?: () => void;
}

const CheckoutComModal3DS: React.FC<ICheckoutComModal3DS> = ({
  on3dsSuccess,
}) => {
  const tCard = useTranslations('Card');
  const { authUser } = useAuthentication();
  const { checkoutCart } = useCheckoutCartState();
  const { state, send } = useCheckoutComMachine();

  useEffect(() => {
    if (
      state.value === CHECKOUT_COM_CARD_FORM_STATES['3D_SECURE_OPEN'] &&
      isMobileApp
    ) {
      sendMessageToMobile('3dsOpened');
    }
  }, [state.value]);

  const handle3dsMessage = useCallback(
    async (event: MessageEvent) => {
      if (
        event.data.type === LF_WINDOW_MESSAGE &&
        state.value === CHECKOUT_COM_CARD_FORM_STATES['3D_SECURE_OPEN']
      ) {
        if (event.data.paymentStatus === SubscriptionStatus.SUCCESS) {
          send({ type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.VALIDATE_3DS });
          on3dsSuccess?.();
        } else {
          toast(
            event.data.paymentErrorMessage ||
              tCard('cardAuthentificationFailed'),
            {
              className: 'toast-error-pause',
              autoClose: false,
            },
          );
          send({
            type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.RESET,
          });
        }
      }
    },
    [state.context.paymentId, state.value],
  );

  useEffect(() => {
    window.addEventListener('message', handle3dsMessage);

    return () => {
      window.removeEventListener('message', handle3dsMessage);
    };
  }, [handle3dsMessage]);

  return (
    <Modal
      centered
      isSafelyClosable
      isOpen={state.context.is3DSecureOpen}
      UNSAFE_className="threeDS-modal tw-w-full tw-max-w-[500px]"
      externalToggle={() => {
        send({
          type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.RESET,
        });

        const warnMessage = {
          message: '[payment] User manually closed 3DS modal.',
          context: {
            user_id: authUser?.uid,
            cart_id: checkoutCart?.id,
          },
        };

        logger.warn(warnMessage.message, warnMessage.context);
      }}
    >
      <iframe
        src={state.context.url3DSecure}
        id="3DS-iframe"
        title="3DS Confirmation"
        height="430"
        width="100%"
        className="tw-h-[530px] tw-rounded-2xl tw-border-0 tw-border-none tw-pt-3 desktop:tw-pl-4"
      />
    </Modal>
  );
};

export default CheckoutComModal3DS;
